/**
 * SEO Structured Data Components (JSON-LD / Schema.org)
 * 
 * Generates rich snippets for Google Search:
 * - SoftwareApplication: App-Info mit Preis in SERPs
 * - BreadcrumbList: Strukturierte Navigation in SERPs
 * - HowTo: Workflow-Schritte in SERPs
 * - Organization: Firmen-Info für Knowledge Panel
 */

const DOMAIN = 'https://ivoliq.io';

export function SoftwareApplicationSchema({ country }) {
  const currency = country === 'CH' ? 'CHF' : 'EUR';
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "ivoliq",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Buchhaltungssoftware für Freelancer",
    "operatingSystem": "Web",
    "url": DOMAIN,
    "description": "End-to-End Workflow für Freelancer: Zeiterfassung, Projektmanagement, Rechnungserstellung – alles in einer App. DACH-konform.",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": country === 'CH' ? "19" : "19",
      "priceCurrency": currency,
      "offerCount": "3",
      "offers": [
        {
          "@type": "Offer",
          "name": "Free",
          "price": "0",
          "priceCurrency": currency,
          "description": "Kostenlos für immer – bis zu 3 Kunden, 3 Projekte"
        },
        {
          "@type": "Offer",
          "name": "Basic",
          "price": "9",
          "priceCurrency": currency,
          "description": "Unbegrenzte Kunden & Projekte"
        },
        {
          "@type": "Offer",
          "name": "Pro",
          "price": "19",
          "priceCurrency": currency,
          "description": "Alle Features inkl. Zeiterfassung & Steuerübersicht"
        }
      ]
    },
    "featureList": [
      "Zeiterfassung mit integriertem Timer",
      "Rechnungserstellung mit einem Klick",
      "Projekt- & Kundenverwaltung",
      "Steuerrücklagen-Berechnung (DE/AT/CH)",
      "Swiss QR-Bill Unterstützung",
      "DSGVO-/DSG-konform, EU-Server"
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      ...(item.url ? { "item": `${DOMAIN}${item.url}` } : {})
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function HowToSchema({ name, description, steps, totalTime }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    ...(totalTime ? { "totalTime": totalTime } : {}),
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      ...(step.url ? { "url": `${DOMAIN}${step.url}` } : {})
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ivoliq",
    "legalName": "ivoliq GmbH",
    "url": DOMAIN,
    "description": "Buchhaltungssoftware für Freelancer im DACH-Raum. End-to-End Workflow von Zeiterfassung bis Rechnung.",
    "foundingDate": "2024",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Raffelspergergasse 11/2/2",
      "postalCode": "1190",
      "addressLocality": "Wien",
      "addressCountry": "AT"
    },
    "areaServed": [
      { "@type": "Country", "name": "Deutschland" },
      { "@type": "Country", "name": "Österreich" },
      { "@type": "Country", "name": "Schweiz" }
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "support@ivoliq.io",
      "contactType": "customer support",
      "availableLanguage": "German"
    },
    "sameAs": []
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
